import { ResponsiveBar } from '@nivo/bar';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { TwTheme } from '../../../Tw';
import {
  getBestContrastColor,
  getQualitativeColor,
} from '../../common/color.helper';
import { cn } from '../../common/utils';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import DropdownSelector from '../../components/actions/DropdownSelector';
import * as Icon from '../../components/icons';
import { Container } from '../../components/layout/ContainerFlex';

import { ActionBar } from '../../components/nav/ActionBar';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import { StatListItem } from '../../components/stats/StatListItem';
import { useActualityWarehouseComparison } from '../../feed/hooks/useActualityWarehouseComparison';
import Tooltip from '../../layout/tooltip/ToolTip';
import {
  warehouseSelectedId as selectedWarehouseIdState,
  warehouseList,
} from '../../store/warehouse.state';
import {
  getAxisBottomTickRotation,
  getAxisFontSize,
} from './charts/BarChartHelpers';

// CSS for the scrollbar hiding
const scrollbarHidingStyles = `
  .hide-scrollbar {
    -ms-overflow-style: none;  /* Internet Explorer and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none;  /* Chrome, Safari, Opera */
  }
`;

// KPI Group mapping for comparison categories
const getKPIGroupMapping = () => ({
  velocity: {
    title: 'Velocity & Performance',
    icon: Icon.ChartLineUp,
    metrics: ['netLinesPerHour', 'netUnitsPerHour', 'netJobsPerHour', 'avgTimePerJob'],
  },
  throughput: {
    title: 'Throughput & Volume',
    icon: Icon.ChartBarSquare,
    metrics: ['totalJobs', 'totalOrders', 'totalLines', 'totalUnits'],
  },
  efficiency: {
    title: 'Efficiency & Time',
    icon: Icon.ClockTimer,
    metrics: ['efficiencyRatio', 'utilizationRatio', 'totalWorkedHours', 'totalIdleHours'],
  },
  process: {
    title: 'Process Analysis',
    icon: Icon.WorkflowSquare,
    metrics: ['avgLinesPerJob', 'avgUnitsPerLine', 'avgLocationsPerJob', 'processVariability'],
  },
});

export const WarehouseDashboardCompare: React.FC = () => {
  const { t } = useTranslation('app');
  const warehouses = useRecoilValue(warehouseList);
  const selectedWarehouseId = useRecoilValue(selectedWarehouseIdState);
  const colors = TwTheme.extend.colors;
  
  // Use the new actuality warehouse comparison hook
  const {
    warehouseMetrics,
    kpiGroups,
    loadWarehousesData,
    getComparisonData,
    formatMetricValue,
    isLoading,
    availableWarehouses,
  } = useActualityWarehouseComparison();

  // States for user selections
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  const [selectedKPIGroup, setSelectedKPIGroup] = useState<string>('velocity');
  const [selectedMetric, setSelectedMetric] = useState<string>('netLinesPerHour');
  const [showAllWarehouses, setShowAllWarehouses] = useState<boolean>(false);
  const [warehouseFilter, setWarehouseFilter] = useState<string>('');

  // Track clicks for double-click detection
  const [lastClickedId, setLastClickedId] = useState<string | null>(null);
  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);

  const kpiGroupMapping = getKPIGroupMapping();

  // Filter warehouses based on filter text
  const filteredWarehouses = useMemo(() => {
    if (!warehouses) return [];
    return warehouses.filter(wh =>
      warehouseFilter === '' ||
      wh.title.toLowerCase().includes(warehouseFilter.toLowerCase())
    );
  }, [warehouses, warehouseFilter]);

  // Set default selected warehouse when warehouses are loaded
  useEffect(() => {
    if (filteredWarehouses.length > 0 && selectedWarehouses.length === 0) {
      const targetWarehouseId = selectedWarehouseId || filteredWarehouses[0].id;
      setSelectedWarehouses([targetWarehouseId]);
    }
  }, [filteredWarehouses, selectedWarehouses.length, selectedWarehouseId]);

  // Load data for selected warehouses
  useEffect(() => {
    if (selectedWarehouses.length > 0) {
      loadWarehousesData(selectedWarehouses);
    }
  }, [selectedWarehouses, loadWarehousesData]);

  // Handle warehouse click with double-click detection
  const handleWarehouseClick = (warehouseId: string) => {
    if (lastClickedId === warehouseId && clickTimeout) {
      // Double click detected
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      setLastClickedId(null);

      // Set the selected warehouse to only this one
      setSelectedWarehouses([warehouseId]);
    } else {
      // First click - start timer
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }

      setLastClickedId(warehouseId);
      const timeout = setTimeout(() => {
        // Single click action - toggle warehouse selection
        if (selectedWarehouses.includes(warehouseId)) {
          setSelectedWarehouses(
            selectedWarehouses.filter(id => id !== warehouseId),
          );
        } else {
          setSelectedWarehouses([...selectedWarehouses, warehouseId]);
        }

        setClickTimeout(null);
        setLastClickedId(null);
      }, 300); // 300ms is typical double-click threshold

      setClickTimeout(timeout as unknown as NodeJS.Timeout);
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  // Add styles to the document head
  useEffect(() => {
    // Create style element
    const styleElement = document.createElement('style');
    styleElement.innerHTML = scrollbarHidingStyles;
    document.head.appendChild(styleElement);

    // Clean up on unmount
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Get the current KPI group and metric info
  const activeKPIGroup = kpiGroups.find(group => group.id === selectedKPIGroup);
  const activeMetric = activeKPIGroup?.metrics.find(m => m.key === selectedMetric);

  // Get the data formatted for the chart
  const getChartData = () => {
    if (!activeKPIGroup || !activeMetric) return [];

    return getComparisonData(selectedWarehouses, selectedKPIGroup as any, selectedMetric).map(item => ({
      id: item.warehouseId,
      warehouse: item.warehouseName,
      value: item.value,
      color: getQualitativeColor(item.warehouseId, 'dimension')[0],
      unit: item.unit,
      label: activeMetric?.title || '',
    }));
  };

  // Calculate chart label/axis settings based on number of items
  const chartData = getChartData();
  const numItems = chartData.length;
  const axisBottomTickRotation = getAxisBottomTickRotation(numItems, 20, 90, 0);
  const axisFontSize = getAxisFontSize(numItems, 8, 14, 24);

  // Get warehouse color for buttons/legends
  const getWarehouseColor = (warehouseId: string): [string, string] => {
    const color = getQualitativeColor(warehouseId, 'dimension')[0];
    return [color, '#FFFFFF']; // Color and contrast color
  };

  // Display loading state if no warehouses are available yet
  if (!warehouses || warehouses.length === 0) {
    return (
      <Container col hasOverflowY className="gap-4 p-4">
        <h2 className="text-2xl font-bold">{t('Warehouse Comparison')}</h2>
        <div className="flex items-center justify-center p-10">
          <div className="text-lg">{t('Loading warehouse data...')}</div>
        </div>
      </Container>
    );
  }

  return (
    <Container col hasOverflowY className="gap-1">
      <ActionBar
        sticky
        stickyTop
        className="!bg-app-panel-stat/80 flex w-full flex-col gap-4 p-3"
      >
        {/* Warehouse Selection */}
        <div className="flex w-full gap-2 overflow-x-auto px-2 pb-1">
          {filteredWarehouses.map(wh => (
            <div key={wh.id} className="min-w-[220px] flex-1 pb-2">
              <Stat
                hasHelper
                isPreview
                isActionable
                isSelected={selectedWarehouses.includes(wh.id)}
                onClick={() => handleWarehouseClick(wh.id)}
                title={wh.title}
                className="!px-2 !pb-1 !pt-2 text-center"
                legendColor={
                  selectedWarehouses.includes(wh.id)
                    ? getWarehouseColor(wh.id)
                    : null
                }
              >
                <div className="text-center">
                  <Icon.DashboardHomeEmpty className="h-8 w-8 fill-current" />
                  {warehouseMetrics[wh.id]?.loadStatus === 'loading' && (
                    <div className="text-xs text-gray-500">Loading...</div>
                  )}
                  {warehouseMetrics[wh.id]?.loadStatus === 'loaded' && (
                    <div className="text-xs text-green-600">✓ Loaded</div>
                  )}
                  {warehouseMetrics[wh.id]?.loadStatus === 'error' && (
                    <div className="text-xs text-red-600">Error</div>
                  )}
                </div>
              </Stat>
            </div>
          ))}
        </div>

        {/* KPI Group and Metric Selection */}
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex flex-1 flex-col">
            <label className="mb-1 text-xs uppercase">{t('KPI Group')}</label>
            <ButtonSwitchMulti
              autoSize
              buttonType="secondary"
              options={Object.keys(kpiGroupMapping).map(key => ({ 
                label: kpiGroupMapping[key as keyof typeof kpiGroupMapping].title 
              }))}
              selectedIndex={Object.keys(kpiGroupMapping).indexOf(selectedKPIGroup)}
              onClick={index =>
                setSelectedKPIGroup(Object.keys(kpiGroupMapping)[index])
              }
            />
          </div>
          
          {activeKPIGroup && (
            <div className="flex flex-1 flex-col">
              <label className="mb-1 text-xs uppercase">{t('Metric')}</label>
              <DropdownSelector
                value={selectedMetric}
                values={activeKPIGroup.metrics.map(m => m.key)}
                renderValue={key => activeKPIGroup.metrics.find(m => m.key === key)?.title || key}
                                 onChange={(metric) => setSelectedMetric(metric)}
                className="min-w-[200px]"
              />
            </div>
          )}
        </div>
      </ActionBar>

      {/* Main Comparison Chart */}
      <StatGroup title={activeKPIGroup?.title || t('Comparison')} classNameStatGroupItems="flex flex-col">
                  <Stat
            hasHelper
            isPreview
            title={activeMetric?.title || selectedMetric}
            icon={Icon.ChartBarSquare}
          >
          <div className="h-[20rem] w-full flex-1">
            {chartData.length > 0 ? (
              <ResponsiveBar
                data={chartData}
                keys={['value']}
                indexBy="warehouse"
                margin={{ top: 20, right: 20, bottom: 40, left: 60 }}
                padding={0.3}
                valueScale={{ type: 'linear' }}
                valueFormat={value => 
                  formatMetricValue(value as number, activeMetric?.format || 'decimal', activeMetric?.unit)
                }
                colors={({ data }) => data.color}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: axisBottomTickRotation,
                  legendPosition: 'middle',
                  legendOffset: 50,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: `${activeMetric?.title || selectedMetric} ${activeMetric?.unit ? `(${activeMetric.unit})` : ''}`,
                  legendPosition: 'middle',
                  legendOffset: -50,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor={({ color }) => getBestContrastColor(color)}
                role="application"
                ariaLabel={`Comparison of ${activeMetric?.title || selectedMetric} across warehouses`}
                barAriaLabel={e =>
                  `${e.indexValue}: ${e.value} ${activeMetric?.unit || ''}`
                }
                tooltip={({ data }) => (
                  <Tooltip
                    label={data.warehouse}
                    value={`${formatMetricValue(data.value, activeMetric?.format || 'decimal', activeMetric?.unit)}`}
                  />
                )}
                theme={{
                  axis: {
                    domain: {
                      line: {
                        stroke: colors.chart.grid,
                        strokeWidth: 1,
                      },
                    },
                    ticks: {
                      line: {
                        stroke: colors.chart.ticks,
                        strokeWidth: 1,
                      },
                      text: {
                        fill: colors.menu.text,
                        fontSize: axisFontSize,
                      },
                    },
                    legend: {
                      text: {
                        fill: colors.chart.legend,
                        fontSize: Math.max(axisFontSize, 12),
                      },
                    },
                  },
                  labels: {
                    text: {
                      fontSize: 36,
                      fill: colors.menu.text,
                    },
                  },
                  grid: {
                    line: {
                      stroke: colors.chart.grid,
                      strokeWidth: 1,
                    },
                  },
                }}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                                     {isLoading ? (
                     <div className="text-lg">{t('Loading comparison data...')}</div>
                   ) : (
                    <>
                      <div className="text-lg font-medium">No data available</div>
                      <div className="text-sm text-gray-500">Select warehouses to compare metrics</div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </Stat>
      </StatGroup>

      {/* KPI Group Overview */}
      {activeKPIGroup && (
        <StatGroup
          title={`${activeKPIGroup.title} - All Metrics`}
          classNameStatGroupItems={cn(
            `!my-0 !space-y-0 grid gap-1`,
            `grid-cols-${Math.min(activeKPIGroup.metrics.length, 4)}`,
            `md:grid-cols-${Math.min(activeKPIGroup.metrics.length, 4)}`,
            `lg:grid-cols-${Math.min(activeKPIGroup.metrics.length, 4)}`,
          )}
          className="!my-0"
        >
          {activeKPIGroup.metrics.map(metric => {
            const data = getComparisonData(selectedWarehouses, selectedKPIGroup as any, metric.key);
            const avgValue = data.length > 0 ? _.meanBy(data, 'value') : 0;
            
            return (
              <Stat
                key={metric.key}
                hasHelper
                isPreview
                title={metric.title}
                value={formatMetricValue(avgValue, metric.format, metric.unit)}
                unitOfMeasure={metric.unit}
              >
                {data.map(item => (
                  <StatListItem
                    key={item.warehouseId}
                    title={item.warehouseName}
                    value={formatMetricValue(item.value, metric.format, metric.unit)}
                    unitOfMeasure={metric.unit}
                  />
                ))}
              </Stat>
            );
          })}
        </StatGroup>
      )}

             {isLoading && (
         <div className="flex items-center justify-center p-8">
           <div className="text-lg">{t('Loading actuality data...')}</div>
         </div>
       )}
    </Container>
  );
};

export default WarehouseDashboardCompare;
